import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

import {
  GetSongpopMaestroFeatureQuery,
  UpdateSongpopMaestroConfigMutation,
  UpdateSongpopMaestroConfigMutationVariables,
} from '../../../../../../__gqltypes__';
import { SONGPOP_MAESTRO_FEATURE_QUERY, UPDATE_SONGPOP_MAESTRO_CONFIG } from '../graphql';

/**
 * Master switch of the whole feature (GameConfig features.songpopMaestro `enabled`), checked before
 * any drop schedule: sales open and close with the events. Saved on its own, so toggling it never
 * resends the perk values of the Settings tab.
 */
export function MaestroPackFeatureSwitch() {
  const { data, loading, refetch } = useQuery<GetSongpopMaestroFeatureQuery>(SONGPOP_MAESTRO_FEATURE_QUERY, {
    fetchPolicy: 'no-cache',
  });
  const [updateConfig, { loading: saving, error }] = useMutation<
    UpdateSongpopMaestroConfigMutation,
    UpdateSongpopMaestroConfigMutationVariables
  >(UPDATE_SONGPOP_MAESTRO_CONFIG, { onCompleted: () => refetch() });
  const [pending, setPending] = useState<boolean | null>(null);

  const enabled = data?.liveOps.songpopMaestroConfig.enabled;

  const apply = () => {
    if (pending === null) {
      return;
    }
    updateConfig({ variables: { input: { enabled: pending } } })
      .catch(() => {
        // surfaced through error
      })
      .finally(() => setPending(null));
  };

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center">
        <h4 className="mb-0 mr-3">Songpop Maestro</h4>
        {loading || enabled === undefined ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <Form.Check
              type="switch"
              id="maestro-feature-enabled"
              label=""
              checked={enabled}
              disabled={saving}
              onChange={(e) => setPending(e.target.checked)}
            />
            <Badge variant={enabled ? 'success' : 'secondary'}>
              {enabled ? 'ON: visible in the app' : 'OFF: hidden from every player'}
            </Badge>
            {saving && <Spinner animation="border" size="sm" className="ml-2" />}
          </>
        )}
      </div>
      <small className="text-muted d-block">
        Master switch of the whole feature. <strong>ON</strong>: Songpop Maestro is live; each event sells on its own
        schedule. <strong>OFF</strong>: everything Songpop Maestro is hidden from every player, owners included: offer,
        shop card, pushes, perks, Maestro leaderboards and their rewards, Founders wall, badges and MAESTRO Gacha boxes.
        Nothing is lost: passes, scores and rewards come back when it is turned on again, and a payment in progress is
        still granted.
      </small>
      {error && (
        <Alert variant="danger" className="mt-2">
          {error.message}
        </Alert>
      )}

      <Modal show={pending !== null} onHide={() => setPending(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{pending ? 'Turn Songpop Maestro on?' : 'Turn Songpop Maestro off?'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pending
            ? 'Songpop Maestro shows in the app again: owners get their perks, Maestro leaderboards, Founders wall and badges back, and every running event starts selling to its audience. It can take a few minutes to show in the app.'
            : 'Songpop Maestro disappears for every player, owners included: no offer, no pushes, no perks, no Maestro leaderboards, Founders wall, badges or MAESTRO Gacha boxes. Nothing is deleted: it all comes back when turned on again.'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button variant={pending ? 'success' : 'danger'} onClick={apply}>
            {pending ? 'Turn Songpop Maestro on' : 'Turn Songpop Maestro off'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
